/**
 * @rogueworlds/client — the main-thread side of the mesher worker pool.
 *
 * Jobs are queued and dispatched round-robin to idle workers, one job per worker at a time. Results
 * arrive already transferred (the worker posts its `MeshData` buffers, it does not clone them), so
 * the only per-chunk main-thread work left is building the `BufferGeometry`.
 *
 * Failure handling is explicit because a worker is an opaque black box from here: a job that throws
 * inside the worker arrives as an `MesherErrorFrame` carrying its job id, while a worker that dies
 * (`onerror`) or fails to deserialise a message (`onmessageerror`) is blamed on the job it was
 * running, terminated and replaced, so one poisoned chunk cannot stall the pool for the session.
 */

import type { MesherErrorFrame, MesherJob, MesherResult } from "./types";

/** Upper bound on workers: meshing is bursty and each worker costs a thread plus a chunk of heap. */
const MAX_WORKERS = 4;
/** Fallback when `navigator.hardwareConcurrency` is unavailable. */
const FALLBACK_CORES = 4;

/** Default pool size: leave one core for the render loop and the network. */
function defaultWorkerCount(): number {
  return Math.max(1, Math.min(MAX_WORKERS, (navigator.hardwareConcurrency ?? FALLBACK_CORES) - 1));
}

interface PoolWorker {
  worker: Worker;
  /** Job currently executing on this worker, or null when it is idle. */
  jobId: number | null;
}

export class MesherPool {
  private readonly pool: PoolWorker[] = [];
  /** Jobs accepted but not yet dispatched, in submission order. */
  private readonly queue: MesherJob[] = [];
  private resultCallback: ((result: MesherResult) => void) | null = null;
  private errorCallback: ((error: Error, jobId: number) => void) | null = null;
  /** Round-robin cursor: the next dispatch starts scanning from here. */
  private cursor = 0;
  private running = 0;
  private disposed = false;

  constructor(workerCount: number = defaultWorkerCount()) {
    const count = Math.max(1, Math.floor(workerCount));
    for (let index = 0; index < count; index++) {
      this.pool.push({ worker: this.spawn(index), jobId: null });
    }
  }

  /** Queues one job and dispatches as many as there are idle workers. */
  submit(job: MesherJob): void {
    if (this.disposed) {
      // Surfaced through `onError` rather than thrown: a caller in a frame loop must not crash.
      this.errorCallback?.(new Error("MesherPool: submit() after dispose()"), job.id);
      return;
    }
    this.queue.push(job);
    this.drain();
  }

  /** Replaces the result callback (the pool keeps exactly one). */
  onResult(callback: (result: MesherResult) => void): void {
    this.resultCallback = callback;
  }

  /** Replaces the failure callback (the pool keeps exactly one). */
  onError(callback: (error: Error, jobId: number) => void): void {
    this.errorCallback = callback;
  }

  /** Queued + in-flight jobs. Diagnostics and back-pressure both need this. */
  get pending(): number {
    return this.queue.length + this.running;
  }

  get workers(): number {
    return this.pool.length;
  }

  /**
   * Terminates every worker and reports every outstanding job as failed. Idempotent. After this the
   * pool accepts no further work and holds no callbacks.
   */
  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    const cancelled = new Error("MesherPool: job rejected because the pool was disposed");
    for (const job of this.queue) {
      this.errorCallback?.(cancelled, job.id);
    }
    this.queue.length = 0;
    for (const entry of this.pool) {
      if (entry.jobId !== null) {
        this.errorCallback?.(cancelled, entry.jobId);
        entry.jobId = null;
      }
      entry.worker.terminate();
    }
    this.pool.length = 0;
    this.running = 0;
    this.resultCallback = null;
    this.errorCallback = null;
  }

  /** Creates a worker and wires its three message channels. */
  private spawn(index: number): Worker {
    const worker = new Worker(new URL("./mesher.worker.ts", import.meta.url), { type: "module" });
    worker.onmessage = (event: MessageEvent<MesherResult | MesherErrorFrame>): void => {
      this.handleMessage(index, event.data);
    };
    worker.onmessageerror = (): void => {
      this.failWorker(index, new Error("mesher worker could not deserialise a message"));
    };
    worker.onerror = (event: ErrorEvent): void => {
      this.failWorker(index, new Error(event.message === "" ? "mesher worker crashed" : event.message));
    };
    return worker;
  }

  /** Dispatches queued jobs to idle workers, round-robin, until one side runs out. */
  private drain(): void {
    if (this.disposed) {
      return;
    }
    while (this.queue.length > 0) {
      const index = this.nextIdleWorker();
      if (index < 0) {
        return;
      }
      const job = this.queue.shift();
      if (job === undefined) {
        return;
      }
      const entry = this.pool[index];
      if (entry === undefined) {
        // Unreachable: `index` comes from a scan over `this.pool`.
        return;
      }
      entry.jobId = job.id;
      this.running += 1;
      entry.worker.postMessage(job);
    }
  }

  /** Index of the next idle worker from the cursor, or -1 when all are busy. */
  private nextIdleWorker(): number {
    const count = this.pool.length;
    for (let step = 0; step < count; step++) {
      const index = (this.cursor + step) % count;
      const entry = this.pool[index];
      if (entry !== undefined && entry.jobId === null) {
        this.cursor = (index + 1) % count;
        return index;
      }
    }
    return -1;
  }

  private handleMessage(index: number, data: MesherResult | MesherErrorFrame): void {
    const jobId = this.release(index);
    if ("error" in data) {
      this.errorCallback?.(new Error(`mesher job ${data.id} failed: ${data.error}`), data.id);
    } else if (jobId !== null) {
      this.resultCallback?.(data);
    }
    this.drain();
  }

  /**
   * Blames a worker-level failure on the job it was running, then replaces the worker: after an
   * `error` event the worker's state is unknown, and a wedged worker would silently reduce
   * throughput for the rest of the session.
   */
  private failWorker(index: number, error: Error): void {
    const jobId = this.release(index);
    if (jobId !== null) {
      this.errorCallback?.(error, jobId);
    }
    const entry = this.pool[index];
    if (entry === undefined) {
      // Unreachable: `index` comes from a wired worker.
      return;
    }
    entry.worker.terminate();
    if (!this.disposed) {
      entry.worker = this.spawn(index);
    }
    this.drain();
  }

  /** Marks a worker idle and returns the job it was running (null when it was already idle). */
  private release(index: number): number | null {
    const entry = this.pool[index];
    if (entry === undefined) {
      return null;
    }
    const jobId = entry.jobId;
    entry.jobId = null;
    if (jobId !== null) {
      this.running -= 1;
    }
    return jobId;
  }
}
